import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { TrendingUp, Target, DollarSign, ArrowUpRight, BarChart2, Users } from 'lucide-react';
import type { IconComponent } from '../types';

interface CaseStudy {
  client: string;
  industry: string;
  challenge: string;
  solution: string;
  results: { label: string; value: string; icon: IconComponent }[];
  timeframe: string;
}

const caseStudies: CaseStudy[] = [
  {
    client: 'HomeGoods Brand',
    industry: 'Home & Kitchen',
    challenge: 'ACOS creeping to 28% with stagnant sales growth on Sponsored Products.',
    solution: 'Restructured campaign architecture with AI bid optimization and negative keyword refinement.',
    results: [
      { label: 'ACOS Reduction', value: '28% → 19%', icon: TrendingUp },
      { label: 'Revenue Increase', value: '+34%', icon: DollarSign },
    ],
    timeframe: '4 months',
  },
  {
    client: 'Beauty Startup',
    industry: 'Beauty & Personal Care',
    challenge: 'New brand launch with zero ad history and limited budget.',
    solution: 'Phased launch strategy starting with auto campaigns, graduating to targeted SP and SB.',
    results: [
      { label: 'ROAS Achieved', value: '4.2x', icon: Target },
      { label: 'Organic Rank', value: 'Page 1', icon: BarChart2 },
    ],
    timeframe: '6 months',
  },
  {
    client: 'Electronics Seller',
    industry: 'Consumer Electronics',
    challenge: 'Seasonal inventory management with DSP budget waste on irrelevant audiences.',
    solution: 'AMC audience analysis with DSP retargeting and dayparting adjustments.',
    results: [
      { label: 'DSP Efficiency', value: '+52%', icon: TrendingUp },
      { label: 'Conversion Rate', value: '+18%', icon: Users },
    ],
    timeframe: 'Q4 campaign',
  },
];

export default function CaseStudiesSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      id="case-studies"
      ref={sectionRef}
      className="relative bg-[#05060B] grid-bg py-20 lg:py-24 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(6,182,212,0.06)_0%,transparent_70%)] pointer-events-none" />

      <div className="relative z-10 w-full px-6 lg:px-12 xl:px-20">
        <div className="max-w-7xl mx-auto">
          <div 
            className={`text-center mb-12 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium uppercase tracking-wider mb-6">
              Client Results
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Real outcomes,{' '}
              <span className="text-cyan-400 cyan-glow">real businesses.</span>
            </h2>
            <p className="text-lg text-dark-400 max-w-2xl mx-auto">
              Representative results from brands we've partnered with. Every account is different, but the approach is consistent.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {caseStudies.map((study, index) => (
              <div
                key={index}
                className={`glass-card p-6 group hover-lift cursor-pointer ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150 + 200}ms` }}
              >
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-cyan-400 uppercase tracking-wider">
                      {study.industry}
                    </span>
                    <span className="text-xs text-dark-500">{study.timeframe}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-white group-hover:text-cyan-400 transition-colors">
                    {study.client}
                  </h3>
                </div>

                <div className="space-y-3 mb-6">
                  <div>
                    <span className="text-xs text-dark-500 uppercase tracking-wider">Challenge</span>
                    <p className="text-sm text-dark-300 mt-1">{study.challenge}</p>
                  </div>
                  <div>
                    <span className="text-xs text-dark-500 uppercase tracking-wider">Solution</span>
                    <p className="text-sm text-dark-300 mt-1">{study.solution}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-cyan-500/10">
                  {study.results.map((result, rIndex) => (
                    <div key={rIndex} className="text-center">
                      <result.icon className="w-4 h-4 text-cyan-400 mx-auto mb-1" />
                      <div className="text-base font-bold text-white">{result.value}</div>
                      <div className="text-xs text-dark-500">{result.label}</div>
                    </div>
                  ))}
                </div>

                <button className="w-full mt-4 py-2.5 rounded-lg border border-cyan-500/20 text-cyan-400 text-sm font-medium hover:bg-cyan-500/10 transition-all flex items-center justify-center gap-2">
                  View Full Analysis
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <p 
            className={`text-center text-xs text-dark-500 mt-8 transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100' : 'opacity-0'
            }`}
          >
            * Results vary by account, category, and competitive landscape.
          </p>
        </div>
      </div>
    </section>
  );
}
